import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/store';

import { Icon, PageShell, Toast } from '../../components';
import { useToast } from '../../hooks/useToast';
import { settleAutopilotTopup } from '../blog/autopilotTopup';
import { LogoMarkGlyph } from '../casino-settings/components/Glyphs/Glyphs';
import { BuyCreditsSheet } from '../creatives/components/BuyCreditsSheet';
import { areaDescription, areaTitle } from './area-copy';
import type { WebsiteCardDef } from './cards';
import { ICON_PX, WebsiteHubCard } from './components/WebsiteHubCard';
import { HubGlyph } from './components/HubGlyph';
import { WebsiteHubGrid } from './components/WebsiteHubGrid';
import { WebsiteHubChromeContext, type WebsiteHubChrome } from './hub-chrome';
import { Header, HubSubtitle, HubTitle } from './website-hub.styles';

export type PageWebsiteHubProps = {
  styleText?: string;
  className?: string;
};

/** The viewBox width of the logo mark (64) and of every other glyph (the shared Icon's 24): the ink's sweep spans it. */
const LOGO_MARK_SPAN = 64;
const ICON_SPAN = 24;

/** One area's card: its summary hook (fixed per card, so always called), its copy and its icon, painted on the
 *  neutral tile in its area's sweep, the hue of the card's glow. */
const HubCardSlot = ({ card, index }: { card: WebsiteCardDef; index: number }): React.ReactElement => {
  const { t } = useTranslation();
  const { useSummary } = card;
  const view = useSummary();
  const glyph = card.icon;
  const icon =
    glyph === 'logo-mark' ? (
      <HubGlyph area={card.id} span={LOGO_MARK_SPAN}>
        {(ink) => <LogoMarkGlyph size={ICON_PX} color={ink} />}
      </HubGlyph>
    ) : (
      <HubGlyph area={card.id} span={ICON_SPAN}>
        {(ink) => <Icon name={glyph} size={ICON_PX} color={ink} />}
      </HubGlyph>
    );
  return (
    <WebsiteHubCard
      area={card.id}
      title={areaTitle(t, card.id)}
      description={areaDescription(t, card.id)}
      icon={icon}
      to={card.path}
      view={view}
      enterIndex={index}
      testId={`hub-card-${card.id}`}
    />
  );
};

type BuyCreditsState = { open: boolean; suggested?: number };

/**
 * The Website hub (`/account/website`): one card per Website area, in the areas' order, each saying whether
 * something there waits for the owner and offering the actions that fit. Each card loads its own data; the page
 * owns only what cards share: one toast and the buy-credits sheet (mounted here rather than through
 * `?credits=buy`, which opens once per layout mount), settled as Home settles it.
 */
const PageWebsiteHub = ({ styleText = '', className }: PageWebsiteHubProps): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { toast, show, dismiss } = useToast();
  const [buyCredits, setBuyCredits] = React.useState<BuyCreditsState>({ open: false });

  const openBuyCredits = React.useCallback((suggested?: number) => setBuyCredits({ open: true, suggested }), []);
  const closeBuyCredits = React.useCallback(() => setBuyCredits({ open: false }), []);
  const onCreditsBought = React.useCallback(() => {
    void settleAutopilotTopup(dispatch, { walletFresh: true });
  }, [dispatch]);
  // Stable while the page lives, so a card hook that keeps `notify` or `openBuyCredits` in its effect deps does not
  // re-run on every toast or sheet change.
  const chrome = React.useMemo<WebsiteHubChrome>(
    () => ({
      notify: (message, placeholders) => show(message, placeholders ? { placeholders } : undefined),
      openBuyCredits,
    }),
    [show, openBuyCredits],
  );

  return (
    <WebsiteHubChromeContext.Provider value={chrome}>
      <PageShell styleText={styleText} className={className}>
        <Header>
          <HubTitle text="Website" tag="h1" />
          <HubSubtitle text="Everything your players see on your casino site." tag="paragraph" />
        </Header>
        <WebsiteHubGrid renderCard={(card, index) => <HubCardSlot key={card.id} card={card} index={index} />} />
        <Toast toast={toast} onDismiss={dismiss} />
        {buyCredits.open ? (
          <BuyCreditsSheet
            suggestedCredits={buyCredits.suggested}
            onClose={closeBuyCredits}
            onSuccess={onCreditsBought}
          />
        ) : null}
      </PageShell>
    </WebsiteHubChromeContext.Provider>
  );
};

export default PageWebsiteHub;
